import { z } from "zod";

/**
 * Common validation schemas shared across all modules
 * These schemas are used for consistent validation across the platform
 */

// ============= Common Schemas =============

/**
 * Schema for integer IDs
 * Validates that the ID is a positive integer
 */
export const IntIdSchema = z.number().int().positive();

/**
 * Schema for boolean values from string inputs (URL params/query)
 * Accepts only "true" or "false" strings and converts them to boolean
 */
export const BooleanSchema = z.enum(["true", "false"]).transform((val: "true" | "false") => val === "true");

/**
 * Schema for ISO date format (YYYY-MM-DD).
 * z.iso.date() exclusively accepts YYYY-MM-DD and validates calendar correctness
 * (e.g. 2026-02-30 is rejected). Replaces the previous regex-based validation.
 */
export const YYYYMMDDSchema = z.iso.date().describe("ISO date in YYYY-MM-DD format");

export const YYYYMMDDTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, "Must be in YYYY-MM-DD HH:MM:SS format");

/**
 * Schema for non-negative integers
 * Commonly used for counts, months, etc.
 */
export const NonNegativeIntSchema = z.number().int().min(0);

/**
 * Schema for non-empty strings
 * Ensures the string is not empty
 */
export const NonEmptyStringSchema = z.string().min(1, "Field is required");

/**
 * Schema for non-empty string arrays
 * Filters out empty values and ensures array contains non-empty strings
 */
export const NonEmptyStringArraySchema = z
  .array(z.string())
  .transform((arr) => arr.filter((s) => s.length > 0))
  .pipe(z.array(z.string().min(1)));

/**
 * Schema for email validation
 * Validates that the string is a properly formatted email address
 */
export const EmailSchema = z.string().email("Must be a valid email address");

/**
 * Schema for phone numbers
 * Ensures the phone number is not empty
 */
export const PhoneSchema = z.string().min(1, "Phone number is required");

/**
 * Schema for UUID validation
 * Uses z.guid() instead of z.uuid() to accept any GUID/UUID-shaped string
 * (8-4-4-4-12 hex groups) without enforcing RFC 4122 version or variant bits.
 * This allows non-standard UUIDs (e.g. from external systems) to pass validation.
 */
export const UUIDSchema = z.guid();

// ============= Common Param Schemas =============

/**
 * Schema for integer IDs from string inputs (URL params/query)
 * Validates that the ID is a non-negative integer (>= 0)
 * Uses pipe for better error messages and type safety
 */
export const StringToIntIdSchema = z.coerce.number().int().nonnegative();

/**
 * Schema for routes with crewId parameter
 */
export const CrewIdParamSchema = z.object({
  crewId: StringToIntIdSchema,
});

/**
 * Schema for routes with userId parameter
 */
export const UserIdParamSchema = z.object({
  userId: StringToIntIdSchema,
});

/**
 * Schema for routes with both crewId and userId parameters
 */
export const CrewUserIdParamSchema = z.object({
  crewId: StringToIntIdSchema,
  userId: StringToIntIdSchema,
});

// Type exports
export type CrewIdParam = z.infer<typeof CrewIdParamSchema>;
export type UserIdParam = z.infer<typeof UserIdParamSchema>;
export type CrewUserIdParam = z.infer<typeof CrewUserIdParamSchema>;

// ============= Common Response Schemas =============

/**
 * Simple response schema for operations that return success status
 */
export const SimpleResponseSchema = z.object({
  ok: z.boolean(),
});

export type SimpleResponse = z.infer<typeof SimpleResponseSchema>;

/**
 * Redirect response schema for HTTP 302 redirects
 * This is a marker schema used by OpenAPI generator to document redirect endpoints
 * The actual Express response uses res.redirect() which returns a 302 status
 */
export const RedirectResponseSchema = z.object({
  location: z.string().url().describe("The URL to redirect to"),
});

export type RedirectResponse = z.infer<typeof RedirectResponseSchema>;

/**
 * URL response schema for operations that return a URL
 * Commonly used for endpoints that return a URL to redirect to or navigate to
 */
export const UrlResponseSchema = z.object({
  url: z.string().url().describe("The URL to navigate to"),
});

export type UrlResponse = z.infer<typeof UrlResponseSchema>;
